import React, { useState, useEffect } from 'react';
import { X, Edit2, Trash2, Plus, Search, UserPlus, UserMinus, ChevronDown, Layers, Users, GraduationCap, RefreshCw } from 'lucide-react';

const GroupForm = ({ mode = 'new', group = null, users = [], programs = [], onClose, onSubmit, groups = [], onEdit, onDelete }) => {
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [includedUsers, setIncludedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchContext, setSearchContext] = useState('all');
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    try {
      // Ensure users is always an array
      const usersArray = Array.isArray(users) ? users : [];
      
      if (mode === 'edit' || mode === 'view') {
        setTitle(group?.title || '');
        setCode(group?.code || '');
        setSelectedProgram(group?.program?._id || group?.program || '');
        const included = usersArray.filter(u => group?.users?.some(id => (u._id === id || (typeof id === 'object' && u._id === id._id))));
        setIncludedUsers(included);
        const available = usersArray.filter(u => !group?.users?.some(id => (u._id === id || (typeof id === 'object' && u._id === id._id))));
        setAvailableUsers(available);
      } else {
        setTitle('');
        setCode('');
        setSelectedProgram('');
        setIncludedUsers([]);
        setAvailableUsers(usersArray);
      }
      setErrors({});
      setSearchQuery('');
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, [mode, group, users]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';
  const isList = mode === 'list';

  const generateCode = () => {
    // Generate a random 8-character alphanumeric code
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setCode(result);
  };

  const handleUserToggle = (user, isIncluded) => {
    if (isIncluded) {
      setIncludedUsers(includedUsers.filter(u => u._id !== user._id));
      setAvailableUsers([...availableUsers, user]);
    } else {
      setIncludedUsers([...includedUsers, user]);
      setAvailableUsers(availableUsers.filter(u => u._id !== user._id));
    }
  };

  const getSearchPlaceholder = () => {
    switch (searchContext) {
      case 'included':
        return 'Search in included users...';
      case 'available':
        return 'Search in available users...';
      default:
        return 'Search all users...';
    }
  };

  const getSearchContextLabel = () => {
    switch (searchContext) {
      case 'included':
        return 'Included Users';
      case 'available':
        return 'Available Users';
      default:
        return 'All Users';
    }
  };

  const handleSearchContextChange = (context) => {
    setSearchContext(context);
    setIsSearchDropdownOpen(false);
  };

  // Role badge utility functions (consistent with ManageUsersTab)
  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'instructor':
        return 'bg-yellow-100 text-yellow-800';
      case 'student':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadge = (role) => {
    return (
      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getRoleBadgeStyle(role)}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  // Role counting utility function
  const getRoleCounts = (users = []) => {
    return users.reduce((counts, user) => {
      const role = user.role || 'visitor';
      counts[role] = (counts[role] || 0) + 1;
      return counts;
    }, {});
  };

  const filteredAvailableUsers = searchQuery && (searchContext === 'all' || searchContext === 'available')
    ? availableUsers.filter(user =>
        `${user.name} ${user.lastName} ${user.email}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : searchContext === 'included' ? [] : availableUsers;

  const filteredIncludedUsers = searchQuery && (searchContext === 'all' || searchContext === 'included')
    ? includedUsers.filter(user =>
        `${user.name} ${user.lastName} ${user.email}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : searchContext === 'available' ? [] : includedUsers;

  const handleDeleteClick = (group) => {
    setGroupToDelete(group);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(groupToDelete);
    setShowDeleteConfirmation(false);
    setGroupToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setGroupToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (!title.trim()) {
      newErrors.title = 'Group title is required';
    }
    if (!selectedProgram) {
      newErrors.program = 'Program selection is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);
    
    try {
      await onSubmit({ 
        title, 
        code: code || undefined,
        program: selectedProgram,
        users: includedUsers.map(u => u._id) 
      });
      
      setShowSuccess(true);
      setTimeout(() => {
        if (isNew) {
          setTitle('');
          setCode('');
          setSelectedProgram('');
          setIncludedUsers([]);
          setAvailableUsers(users);
          setShowSuccess(false);
        }
        onClose();
      }, 1500);
    } catch (error) {
      // Check if it's a duplicate code error
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to save group. Please try again.';
      
      if (errorMessage.toLowerCase().includes('code already exists')) {
        setErrors({ code: errorMessage });
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isList) {
    return (
      <div className="mt-8">
        {/* Header and Buttons Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-[#054581]">Groups Management</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              className="bg-[#1086a4] text-white px-4 sm:px-6 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center justify-center"
              onClick={() => onEdit(null)}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add new group
            </button>
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {groups.map((group) => (
            <div key={group._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-5 h-5 text-[#1086a4]" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#054581] mb-2">{group.title}</h3>
                    <div className="space-y-2">
                      {group.code && (
                        <div className="text-sm">
                          <span className="font-medium text-[#1e2d40]">Code:</span>{' '}
                          <span className="text-[#1086a4] font-mono">{group.code}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <GraduationCap className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-[#1e2d40]">
                          Program: {group.program?.title || 'No program assigned'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-[#1e2d40]">
                          {(() => {
                            const counts = getRoleCounts(group.users);
                            const studentCount = counts.student || 0;
                            const instructorCount = counts.instructor || 0;
                            return `${studentCount} Students, ${instructorCount} Instructors`;
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Bar */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => onEdit(group)}
                  className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-lg hover:bg-white"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => handleDeleteClick(group)}
                  className="p-2 text-red-500 hover:text-red-700 transition-colors rounded-lg hover:bg-white"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block w-full overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                      Group Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                      Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                      Program
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                      Students / Instructors
                    </th>
                    <th scope="col" className="relative px-6 py-3 w-24">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groups.map((group) => (
                    <tr key={group._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{group.title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-[#1086a4] font-mono">
                          {group.code || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{group.program?.title || 'No program assigned'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            const counts = getRoleCounts(group.users);
                            const studentCount = counts.student || 0;
                            const instructorCount = counts.instructor || 0;
                            return `${studentCount} / ${instructorCount}`;
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => onEdit(group)}
                            className="text-[#1086a4] hover:text-[#054581] transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(group)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
          </table>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg w-full max-w-md mx-4 p-6 border-2 border-[#1086a4]">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-[#054581] mb-4">
                  Delete Group
                </h3>
                <p className="text-gray-600">
                  Are you sure you want to delete the group "{groupToDelete?.title}"? This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 px-4 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                >
                  Delete Group
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Modal Form View
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <form
        className="bg-white rounded-lg shadow-lg w-full relative border-2 border-[#1086a4] max-h-[90vh] overflow-y-auto sm:max-w-lg md:max-w-xl"
        onSubmit={handleSubmit}
      >
        {/* Header */}
        <div className="p-6 border-b border-[#1086a4]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#054581]">
              {isNew && 'Add New Group'}
              {isEdit && 'Edit Group'}
              {isView && 'View Group'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
              disabled={isSubmitting}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="p-4 bg-green-50 border-b border-green-200">
            <p className="text-green-700 text-sm font-medium">
              Group {isNew ? 'created' : 'updated'} successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">Group Title</label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                    errors.title 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  disabled={isView || isSubmitting}
                  placeholder="Enter group title"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className={`flex-1 px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                      errors.code 
                        ? 'border-red-500 focus:border-red-400' 
                        : 'border-[#1086a4] focus:border-[#1086a4]'
                    } focus:outline-none focus:ring-2 ${
                      errors.code ? 'focus:ring-red-500' : 'focus:ring-[#1086a4]'
                    } focus:border-transparent transition-colors`}
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    disabled={isView || isSubmitting}
                    placeholder="Enter or generate code"
                    maxLength={20}
                  />
                  {!isView && (
                    <button
                      type="button"
                      onClick={generateCode}
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Generate Code
                    </button>
                  )}
                </div>
                {errors.code ? (
                  <p className="text-red-500 text-sm mt-1">{errors.code}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">Optional unique identifier for this group</p>
                )}
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">Program *</label>
                <select
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 ${
                    errors.program 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={selectedProgram}
                  onChange={e => setSelectedProgram(e.target.value)}
                  required
                  disabled={isView || isSubmitting}
                >
                  <option value="">Select a program</option>
                  {programs.map(program => (
                    <option key={program._id} value={program._id}>
                      {program.title}
                    </option>
                  ))}
                </select>
                {errors.program && (
                  <p className="text-red-500 text-sm mt-1">{errors.program}</p>
                )}
              </div>
              
              {/* Users Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-[#054581]">Group Members</h3>
                <p className="text-sm text-gray-500">Select users to add to this group. Use the search box to filter users.</p>
                
                {/* Search Box with Dropdown */}
                <div className="relative">
                  <div className="relative flex">
                    {/* Search Context Dropdown Button */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsSearchDropdownOpen(!isSearchDropdownOpen)}
                        className="h-full px-3 py-2 bg-[#1086a4] text-white rounded-l-md border-r border-white/20 hover:bg-[#054581] transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                        disabled={isView || isSubmitting}
                      >
                        <Search className="h-5 w-5" />
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {isSearchDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                          <div className="py-1">
                            <button
                              type="button"
                              onClick={() => handleSearchContextChange('all')}
                              className={`w-full text-left px-4 py-2 text-sm ${
                                searchContext === 'all'
                                  ? 'bg-[#1086a4] text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              All Users
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSearchContextChange('included')}
                              className={`w-full text-left px-4 py-2 text-sm ${
                                searchContext === 'included'
                                  ? 'bg-[#1086a4] text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              Included Users
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSearchContextChange('available')}
                              className={`w-full text-left px-4 py-2 text-sm ${
                                searchContext === 'available'
                                  ? 'bg-[#1086a4] text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              Available Users
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Search Input */}
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        className="w-full pl-3 pr-10 py-2 border border-l-0 border-[#1086a4] rounded-r-md bg-[#f9fafb] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                        placeholder={getSearchPlaceholder()}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={isView || isSubmitting}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* User Lists */}
                <div className="space-y-4">
                  {/* Included Users */}
                  <div className={searchContext === 'available' ? 'opacity-50' : ''}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-[#054581]">Included Users ({includedUsers.length})</h4>
                    </div>
                    <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                      {filteredIncludedUsers.length > 0 ? (
                        filteredIncludedUsers.map(user => (
                          <div
                            key={user._id}
                            className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {user.name} {user.lastName}
                                </p>
                                {getRoleBadge(user.role)}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                            {!isView && (
                              <button
                                type="button"
                                onClick={() => handleUserToggle(user, true)}
                                className="ml-2 p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50 transition-colors"
                              >
                                <UserMinus className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {searchQuery ? 'No matching users found' : 'No users added yet'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Available Users */}
                  <div className={searchContext === 'included' ? 'opacity-50' : ''}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-[#054581]">Available Users ({availableUsers.length})</h4>
                    </div>
                    <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                      {filteredAvailableUsers.length > 0 ? (
                        filteredAvailableUsers.map(user => (
                          <div
                            key={user._id}
                            className="flex items-center justify-between p-2 hover:bg-[#f9fafb] transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {user.name} {user.lastName}
                                </p>
                                {getRoleBadge(user.role)}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                            {!isView && (
                              <button
                                type="button"
                                onClick={() => handleUserToggle(user, false)}
                                className="ml-2 p-1 text-green-500 hover:text-green-700 rounded-full hover:bg-green-50 transition-colors"
                              >
                                <UserPlus className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {searchQuery ? 'No matching users found' : 'No users available'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {(isNew || isEdit) && (
          <div className="border-t border-[#1086a4] p-6">
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isNew ? 'Adding Group...' : 'Saving Changes...'}
                  </>
                ) : (
                  isNew ? 'Add Group' : 'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default GroupForm;
